"use client"
import React, { useEffect, useState, useMemo } from 'react';
import styled from "styled-components";
import "@/app/style/racingCalendar.scss";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LANGTYPE_EN, LangCode, useLanguage } from '@/app/utils/LanguageContext';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayJS, { formattedDate, dateFormat, toDate } from '@/app/utils/DayJS';
import { Dayjs } from 'dayjs';
import { textSize, text_size } from "@/app/utils/MixinSize";

//UI Components
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

//Icons
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from "@mui/material/Box";
import { useMyContext } from '@/app/utils/MyContext';
import { adobeData, defaultValue, generateUrl, getSearchParams, redirectToLink, setRouting } from '@/app/utils/Common';
import { Skeleton } from '@mui/material';
import { MenuTab } from '@/app/utils/class/common/base';
import { menuTabSet } from '@/app/utils/constant/menuTabSet';

type Order = 'default' | 'asc' | 'desc';
const tableRowDisplayCount = 10;

const ViewMoreBtn = styled.div`
    ${text_size(13)}
    height: 32px;
    color: #122C68;
    font-weight: 700;
    cursor: pointer;
    padding: 8px;
    justify-content: center;
    display: flex;
    align-items: center;
`;

interface DisplayRow extends IFixture {
}

function getDisplayTime(lang: LangCode, serverDateTime?: string) {
    return defaultValue(formattedDate(toDate(serverDateTime, dateFormat.cms.datetime), dateFormat.display.time), "-")
}

type propType = {
    isMobile: boolean;
    isCalendarShown: boolean;
    fixtureList?: DisplayRow[]
    config?: any
    filter?: any;
    controlledMonth?: Dayjs; // YYYYMM, controll by filter
    businessDate?: Dayjs;
    onMonthChange?: (month: Dayjs) => void;
    onClickCurrentDay?: () => void;
    isSummary?: boolean; // enter from main page
    isCurMonthEmpty?: boolean; // Fixture Calendar page
};

const RacingCalendar: React.FC<propType> = (props: propType) => {
    const { isMobile, isCalendarShown } = props;
    const { state: lang } = useLanguage();
    const { t } = lang;
    const { state: myContextState, dispatch: myContextDispatch } = useMyContext();

    const serverDateFormat = lang.langType === LANGTYPE_EN ? "M/D/YYYY" : "D/M/YYYY";
    const { RaceDate: urlDate } = getSearchParams(new URLSearchParams(window.location.search));

    const [currentRaceDay, setCurrentRaceDay] = React.useState<boolean>(false);
    const [selectedRaceday, setSelectedRaceDay] = React.useState<string>(""); // use serverDateFormat
    const [displayMonth, setDisplayMonth] = React.useState<string>();
    const [orderBy, setOrderBy] = React.useState<Order>('default');
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [isListExpanded, setIsListExpanded] = useState(false); // control display row count
    // from graphQL
    const [fixtureConfig, setFixtureConfig] = React.useState<any>({});
    const [fixtureList, setFixtureList] = React.useState<DisplayRow[]>([]);

    const [latestMonth, setLatestMonth] = useState<string>(props.businessDate?.format("YYYYMM") || ""); // the month with nearest future race to display

    function dateComparator<T>(a: DisplayRow, b: DisplayRow) {
        if (dayJS((b.date_HK as Value).value, serverDateFormat).isBefore(dayJS((a.date_HK as Value)?.value, serverDateFormat))) {
            return 1;
        }
        if (dayJS((a.date_HK as Value).value, serverDateFormat).isBefore(dayJS((b.date_HK as Value)?.value, serverDateFormat))) {
            return -1;
        }
        return 0
    }

    function customComparator<T>(a: DisplayRow, b: DisplayRow, sortBy: keyof DisplayRow, order: Order, secondSortBy?: keyof DisplayRow) {
        // Check first sort condition
        if (sortBy == "date_HK") {
            return dateComparator(a, b);
        }
        if ((b[sortBy] as Value)?.value! < (a[sortBy] as Value)?.value!) {
            return order == "asc" ? 1 : -1;
        }
        if ((b[sortBy] as Value)?.value! > (a[sortBy] as Value)?.value!) {
            return order == "asc" ? -1 : 1;
        }

        // Check second sort condition
        if (secondSortBy) {
            if (secondSortBy == "date_HK") {
                return dateComparator(a, b);
            }
            if ((b[secondSortBy] as Value)?.value! < (a[secondSortBy] as Value)?.value!) {
                return order == "asc" ? 1 : -1;
            }
            if ((b[secondSortBy] as Value)?.value! > (a[secondSortBy] as Value)?.value!) {
                return order == "asc" ? -1 : 1;
            }
        }
        return 0;
    }

    function getComparator(
        orderBy: Order,
    ): (
        a: DisplayRow,
        b: DisplayRow,
    ) => number {
        if (orderBy == "default") {
            return (a, b) => 0 // keep cms manual sorting order
        }
        return (a, b) => customComparator(a, b, "locationCode", orderBy, "date_HK")
    };

    useEffect(() => {
        setFixtureList(props.fixtureList || []); // update display list if filtered list exist
        if (selectedRaceday?.length && !props?.fixtureList?.find(x => x.date_HK?.value == selectedRaceday)) {
            setSelectedRaceDay(""); //reset selected day when filtered list
        }
    }, [props.fixtureList]);

    useEffect(() => {
        setFixtureConfig(props.config || {});
    }, [props.config]);

    useEffect(() => { //initial month display
        if (props.controlledMonth?.isValid && !props.controlledMonth.isSame(dayJS(displayMonth, "YYYYMM"))) {
            setDisplayMonth(props.controlledMonth.format("YYYYMM")); // need to set format with DD on load
            setLatestMonth(props.controlledMonth.format("YYYYMM"));
        }
    }, [props.controlledMonth, props.businessDate]);

    useEffect(() => {
        if (props.isSummary) {
            setSelectedRaceDay(formattedDate(myContextState.selectedMeetingDate, serverDateFormat));
        }
    }, [myContextState.selectedMeetingDate])

    const handleChangeRaceDay = (day: Dayjs) => {
        if (props.isSummary && globalConfig.OTHERS.IS_INPAGE_REFRESH === "Y") {
            myContextDispatch({type:"changeMtgDate", value:{selectedMeetingDate: day.toDate()}})
        } else {
            const raceSummaryMenuTab = new MenuTab(menuTabSet.RACE_SUMMARY);
            const redirectURL = generateUrl(`/${raceSummaryMenuTab.valueOf()}`, { "RaceDate": day.format("YYYYMMDD"), "Racecourse": "", "RaceNo": "", "redirect": "Y", "focus": "Y" })
            redirectToLink(redirectURL, "_self")
        }
    }

    function ServerDay(DayPickerProps: PickersDayProps<Dayjs>) {
        const { day, outsideCurrentMonth, ...other } = DayPickerProps;

        const isRaceDay = !outsideCurrentMonth && fixtureList.find(fixture => fixture?.date_HK?.value == day.format(serverDateFormat));
        const isSelected = isRaceDay && (day.format(serverDateFormat) == selectedRaceday || props.isSummary && day.isSame(dayJS(urlDate, "YYYYMMDD")));

        return (
            <PickersDay
                sx={{ border: 0, "&:focus.Mui-selected": { backgroundColor: "initial" } }}
                className={`${isRaceDay ? "raceDay " : "nonRaceDay"}${isRaceDay && isSelected ? " selectedDay " : ""}`}
                id={`calendarDate_${day.date()}${outsideCurrentMonth ? "_O" : ""}`}
                {...other}
                outsideCurrentMonth={outsideCurrentMonth} day={day}
                onClick={() => {
                    if (props.isSummary)
                        handleChangeRaceDay(day);
                    else
                        setSelectedRaceDay(selectedRaceday == day.format(serverDateFormat) ? "" : day.format(serverDateFormat));
                    if (!props.isSummary && props.businessDate?.isValid) {
                        setCurrentRaceDay(day.isSame(props.businessDate, "month"));
                    }
                }} />
        );
    };
    // for fixture list table
    function getSelectedDay(element: DisplayRow) {
        if ((!props.isSummary && selectedRaceday != "" || props.isSummary && myContextState.calendarDateSelected) && dayJS(displayMonth, "YYYYMM").isSame(dayJS(selectedRaceday, serverDateFormat), "month")) {
            return dayJS(element?.date_HK?.value, serverDateFormat).format(serverDateFormat) === selectedRaceday; // only show selected day's race
        }

        // Default display: future race only in current month (when no day selected)
        if (props.businessDate?.isSame(dayJS(displayMonth, "YYYYMM"), "month") && !props.isCurMonthEmpty) {
            return dayJS(element?.date_HK?.value, serverDateFormat).format("YYYYMM") == displayMonth && props.businessDate?.isSameOrBefore(dayJS(element?.date_HK?.value, serverDateFormat));
        }
        return dayJS(element?.date_HK?.value, serverDateFormat).format("YYYYMM") == displayMonth // view whole month
    }

    function getDisplayDate(serverDate?: string) {
        return dayJS(serverDate, serverDateFormat).isValid() ? dayJS(serverDate, serverDateFormat).format(dateFormat.display.date) : "-"
    }

    function handleMonthChange(month: Dayjs) {
        props.onMonthChange?.(month);
        if (!props.isSummary) {
            setSelectedRaceDay("");
        }
        setCurrentRaceDay(false);
        setDisplayMonth(dayJS(month).format("YYYYMM"));
    }

    function handleSortByCountry(event: React.MouseEvent<HTMLDivElement>, sortBy: Order = "default") {
        setOrderBy(sortBy);
        setAnchorEl(null);
    };

    const handleViewMore = () => {
        setIsListExpanded(!isListExpanded);
    }

    const handleWAInteraction = () => {
        adobeData(lang.langCode == "en-us" ? "en" : "zh-hk", "interaction", { internalAd: "SPLDSP_racing/simulcast_Calendar" });
    }

    function checkIsWorldPool(raceSeries?: RaceSeriesOption) {
        return raceSeries?.targetItems?.find((x) => x.displayName == "WorldPool");
    }

    const getRaceNameCell = (row: IFixture) => {
        return row?.meetingRaceMapping?.targetItem ? (<Link href={row.redirectLink} onClick={handleWAInteraction}>{row?.raceName?.value}</Link>) : row.raceName?.value
    }

    const countrySortList: { id: Order, itemText: string }[] = [{ id: "default", itemText: t("racingCalendar_SortNone") }, { id: "asc", itemText: "A - Z" }, { id: "desc", itemText: "Z - A" }];

    const filteredRows = useMemo(
        () => {
            return fixtureList?.filter(getSelectedDay) || []
        },
        [fixtureList, displayMonth, selectedRaceday]
    );

    const visibleRows = useMemo(
        () => {
            let sortedList = filteredRows.map(x => x);
            sortedList.sort(getComparator(orderBy));
            return isListExpanded ? sortedList : sortedList.slice(0, tableRowDisplayCount)
        },
        [orderBy, selectedRaceday, displayMonth, fixtureList, isListExpanded]
    );

    const onClickCurrentDay = () => {
        props.onClickCurrentDay && props.onClickCurrentDay();
        if (currentRaceDay) {
            setSelectedRaceDay("");
        }
    }

    return (
        <>
            {displayMonth ?
                <Grid container className={`racingCalendar${isMobile ? " racingCalendar-mobile" : ""}`} id="racingCalendar">
                    {/* Calendar */}
                    {isCalendarShown ? <Grid
                        className="calendarContainer"
                        container
                        item
                        direction="column"
                        sx={{ width: isMobile ? "100%" : "29%" }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={lang.langType === LANGTYPE_EN ? 'en-us' : 'zh-hk'}
                        >
                            <DateCalendar
                                className="calendar"
                                views={["day"]}
                                showDaysOutsideCurrentMonth
                                slots={{
                                    day: ServerDay,
                                }}
                                minDate={dayJS(window.globalConfig?.OTHERS?.CUTOFF_DATE, "YYYYMMDD")}
                                slotProps={{
                                    day: {
                                        selectedRaceday,
                                        isSummary: props.isSummary
                                    } as any,
                                    previousIconButton: {
                                        id: "btnPrevMonth", "title": t("racingCalendar_prev_alt"), "aria-label": t("racingCalendar_prev_alt")
                                    },
                                    nextIconButton: {
                                        id: "btnNextMonth", "title": t("racingCalendar_next_alt"), "aria-label": t("racingCalendar_next_alt")
                                    }
                                }}
                                reduceAnimations
                                onMonthChange={handleMonthChange}
                                value={dayJS(displayMonth?.length == 6 ? displayMonth + "01" : displayMonth, "YYYYMMDD")} // calendar initially can only parse format with date 
                                sx={{
                                    "& .MuiDayCalendar-slideTransition":{
                                        minHeight: "199px"
                                    },
                                    "& .MuiPickersArrowSwitcher-root": {
                                        justifyContent: "space-between",
                                        width: "100%",
                                        "& .MuiPickersArrowSwitcher-button": {
                                            color: "#173E96"
                                        },
                                    },
                                    "& .MuiPickersCalendarHeader-root": {
                                        position: "relative",
                                        margin: "5px 8px",
                                        padding: "0"
                                    },
                                    "& .MuiPickersCalendarHeader-labelContainer": {
                                        ...textSize(15),
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        pointerEvents: "none",
                                    },
                                    "& .MuiDayCalendar-monthContainer": {
                                        borderBottom: "1.18px solid #e2e2e2",
                                        paddingBottom: "6px"
                                    },
                                    "& .MuiDayCalendar-header": {
                                        borderImage: `url(${myContextState.assetPrefix}/borderImage.png) 30`,
                                        borderWidth: "8.23px",
                                        borderStyle: "solid",
                                        borderRight: 0,
                                        borderLeft: 0,
                                        borderTop: 0,
                                    },
                                    "& .MuiPickersDay-dayOutsideMonth": {
                                        color: "#CCCCCC"
                                    }
                                }}
                            />
                        </LocalizationProvider>
                        <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: "2px 14px", ...textSize(11), marginTop: "6px" }}>
                            <Grid xs={5} container item direction="row" justifyContent="flex-start" alignItems="center" id="calendarLegend"><Grid className="raceDayIcon"></Grid><Grid className="raceDayLabel">{t("racingCalendar_race_raceday")}</Grid></Grid>
                            <Grid xs={7} container item direction="row" justifyContent="flex-end" sx={{ textAlign: "end", cursor: "pointer" }}>
                                {!props.isSummary ? <div id="currentDayBtn" onClick={onClickCurrentDay}>{t("racingCalendar_current")}</div> : <div id="currentDayBtn"><Link href={`/${lang.langCode}/overseas`}>{t("racingCalendar_current")}</Link></div>}
                            </Grid>
                        </Grid>
                        <Grid container item direction="row" justifyContent="flex-start" alignItems="center" gap="4px" id="fullSimLink"><img src={`${myContextState.assetPrefix}/fixture/fullFixtureIcon.svg`} /><Link href={`/${lang.langCode}/overseas/simulcast_fixture`} >{t("racingCalendar_fullfixture")}</Link></Grid>
                    </Grid> : null}
                    {/* Table */}
                    <Grid
                        container
                        item
                        direction="column"
                        justifyContent="space-between"
                        md
                        sx={{
                            borderTop: isMobile && isCalendarShown ? "1px solid #B4B4B4" : 0,
                            borderLeft: isMobile ? 0 : "1px solid #B4B4B4"
                        }}>
                        <Grid sx={{
                            borderRadius: "0 0 4px 4px"
                        }}>
                            <TableContainer className={`racingCalendarTable${isMobile ? " racingCalendarTable-mobile" : ""}`}>
                                <Table aria-label="simple table" id="racingCalendarTable">
                                    <colgroup>
                                        {isMobile ? <col width="10%" /> :
                                            <>
                                                <col width="10%" />
                                                <col width="10%" />
                                            </>
                                        }
                                        <col width="60%" />
                                        <col width="20%" />
                                    </colgroup>
                                    <TableHead sx={{
                                        "& .MuiTableCell-root": {
                                            ...textSize(11),
                                            fontWeight: 400,
                                            lineHeight: "14px",
                                        }
                                    }}
                                        id="racingCalendarTableHead">
                                        <TableRow>
                                            {isMobile ?
                                                <TableCell className="centerAlignTableCol" id="calendarDateTimeColHead">{fixtureConfig?.date_HK?.value + "/" + fixtureConfig?.time?.value}</TableCell> :
                                                <>
                                                    <TableCell className="centerAlignTableCol" id="calendarDateColHead">{fixtureConfig?.date_HK?.value}</TableCell>
                                                    <TableCell className="centerAlignTableCol" id="calendarTimeColHead">{fixtureConfig?.time?.value}</TableCell>
                                                </>
                                            }
                                            <TableCell align="left" id="calendarRaceColHead">{fixtureConfig?.raceday_Race?.value}</TableCell>
                                            <TableCell >
                                                <TableSortLabel
                                                    active
                                                    direction="asc"
                                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                        setAnchorEl(anchorEl ? null : event.currentTarget);
                                                    }}
                                                    aria-describedby="country-sort-menu"
                                                    IconComponent={KeyboardArrowUpIcon}
                                                    id="calendarCountryColHead"
                                                >
                                                    {fixtureConfig?.country?.value}
                                                </TableSortLabel>
                                                <Popper
                                                    id="country-sort-menu"
                                                    className="sortPopupMenu"
                                                    open={Boolean(anchorEl)}
                                                    anchorEl={anchorEl}
                                                    placement="bottom-end"
                                                >
                                                    <List
                                                        component="nav"
                                                        aria-label="main mailbox folders"
                                                        subheader={
                                                            <ListSubheader component="div" id="list-subheader"
                                                                className="sortPopupMenuHeader"
                                                            >
                                                                {t("racingCalendar_SortbyCountry")}
                                                            </ListSubheader>
                                                        }>
                                                        {countrySortList.map((item) => (
                                                            <ListItemButton key={item.id}
                                                                className="sortPopupMenuOption"
                                                                onClick={(event: React.MouseEvent<HTMLDivElement>) => { handleSortByCountry(event, item.id); }}
                                                                sx={{
                                                                    padding: "0px 32px",
                                                                    color: "var(--palette-primary-dark)",
                                                                    ...textSize(13),
                                                                    fontWeight: 700,
                                                                    lineHeight: "21px"
                                                                }}
                                                            >
                                                                {orderBy == item.id ? <ListItemIcon sx={{ position: "absolute", left: "10px" }}>
                                                                    <img src={`${myContextState.assetPrefix}/fixture/check.svg`} />
                                                                </ListItemIcon> : null}
                                                                <ListItemText className={`sortPopupMenuText${orderBy == item.id ? "--selected" : ""}`}
                                                                    primary={item.itemText}
                                                                />
                                                            </ListItemButton>
                                                        ))}
                                                    </List>
                                                </Popper >
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody sx={{
                                        "& .MuiTableCell-root": {
                                            ...textSize(13),
                                            fontWeight: 500,
                                            lineHeight: "16px",
                                        }
                                    }}>
                                        {visibleRows.length ? visibleRows.map((row, index) => (
                                            <TableRow key={index} id={`racingCalendarTableRow${index}`}>
                                                {isMobile ?
                                                    <TableCell className="centerAlignTableCol" id={`calendarDateTimeCol_${index}`}>{getDisplayDate(row?.date_HK?.value)} {getDisplayTime(lang.langCode, row?.raceDatetime?.value)}</TableCell> :
                                                    <>
                                                        <TableCell className="centerAlignTableCol" id={`calendarDateCol_${index}`}>{getDisplayDate(row?.date_HK?.value)}</TableCell>
                                                        <TableCell className="centerAlignTableCol time" id={`calendarTimeCol_${index}`}>{getDisplayTime(lang.langCode, row?.raceDatetime?.value)}</TableCell>
                                                    </>
                                                }
                                                <TableCell id={`calendarRaceCol_${index}`}>
                                                    <div>
                                                        {getRaceNameCell(row)}
                                                        {checkIsWorldPool(row.raceSeries) ? <img style={isMobile ? { height: "18px", display: "block" } : { height: "18px", marginLeft: " 5px" }} src={`${myContextState.assetPrefix}/worldpool_400x145_${lang.langType !== LANGTYPE_EN ? "ch" : "en"}.png`} /> : ""}
                                                    </div>
                                                </TableCell>
                                                <TableCell id={`calendarCountryCol_${index}`}>{isMobile ? row?.locationCode?.value : row?.locationName?.value}</TableCell>
                                            </TableRow>
                                        )) :
                                            <TableRow id="racingCalendarEmptyMsg"><TableCell colSpan={4}>{t("racingCalendar_empty_msg")}</TableCell></TableRow>}
                                    </TableBody>
                                </Table>
                                {
                                    filteredRows?.length > tableRowDisplayCount ?
                                        <ViewMoreBtn id="ListViewExpandBtn" onClick={handleViewMore}>
                                            <span>{isListExpanded ? t("viewLess") : t("viewMore")}</span>
                                            <img src={`${myContextState.assetPrefix}/footer/arrow-up.svg`} style={isListExpanded ? { transform: "rotate(0)" } : { transform: "rotate(-180deg)" }} />
                                        </ViewMoreBtn>
                                        : <></>
                                }
                            </TableContainer>
                        </Grid>
                        <Grid>
                            <Box sx={{ display: { xs: "none", lg: "block" } }} id="calendarRemark" className="calendarRemark">{t("racingCalendar_remarks")}</Box>
                        </Grid>
                    </Grid>
                </Grid >
                :
                <Skeleton />
            }
            <Box sx={{ display: { xs: "block", lg: "none" } }} id="calendarRemark" className="calendarRemark mobile">{t("racingCalendar_remarks")}</Box >
        </>
    );
}
export default RacingCalendar;